import { useDataProvider } from "src/system/useDataProvider.ts";
import {
  type DefaultError,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
// todo: useUpdateOne hook should not depend on useResourceForm hook
import type {
  ValidRequestData,
  ValidSaveFormResponseData,
} from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import { useSnackbar } from "src/App/App.hooks.ts";
import { logger } from "src/logger";
import { useTranslation } from "react-i18next";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";

export const useUpdateOne = <
  RequestData extends ValidRequestData = ValidRequestData,
  Data extends ValidSaveFormResponseData = ValidSaveFormResponseData,
>({
  resource,
  disableInvalidation = false,
  tab,
  errorNotification: hookErrorNotification,
  additionalHeaders,
  successNotification: hookSuccessNotification,
  ...options
}: {
  resource: string;
  disableInvalidation?: boolean;
  tab?: string;
  additionalHeaders?: Record<string, any>;
  errorNotification?: {
    message: string;
    flash?: boolean;
  };
  successNotification?: {
    message?: string;
    flash?: boolean;
  };
} & UseMutationOptions<
  Data,
  DefaultError,
  {
    locale: string;
    id: string;
    data: RequestData;
    errorNotification?: {
      message: string;
      flash?: boolean;
    };
    successNotification?: {
      message?: string;
      flash?: boolean;
    };
    disableSuccessNotification?: boolean;
    disableErrorNotification?: boolean;
  }
>) => {
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const queries = useResourceQueries();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: [resource, "updateOne"],
    mutationFn: async ({ locale, id, data }) => {
      try {
        return await dataProvider.updateOne({
          resource: resource,
          data: data,
          tab,
          locale,
          additionalHeaders,
          id,
        });
      } catch (error: any) {
        logger.error(error);
        throw error;
      }
    },
    ...options,
    onError: (data, variables, context) => {
      const { errorNotification, disableErrorNotification } = variables;
      if (!disableErrorNotification) {
        // todo: translate default notification
        const { message = "Couldn't update the record", flash = true } =
          hookErrorNotification || errorNotification || {};
        snackbar.error({
          flash,
          message,
        });
      }
      options?.onError?.(data, variables, context);
    },
    onSuccess: (data, variables, context) => {
      if (!disableInvalidation) {
        queryClient.invalidateQueries({
          queryKey: queries.lists({
            resource,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: queries.detail({
            id: variables.id,
            resource,
          }),
        });
      }

      const { successNotification, disableSuccessNotification } = variables;
      if (!disableSuccessNotification) {
        const {
          flash = true,
          message = t(`notifications.updated`, "Record has been updated", {
            ns: getResourceI18nNamespace(resource),
          }),
        } = hookSuccessNotification || successNotification || {};

        snackbar.success({
          flash,
          message,
        });
      }
      options?.onSuccess?.(data, variables, context);
    },
  });
};
